import {
  designColorSchema,
  type HeaderComponent,
  type SavedDesign,
} from '../schemas/design.js'
import { DEFAULT_DESIGN_DATA } from './default-design.data.js'

/**
 * The stock design for the public trial page and for newly created
 * agencies. The document itself lives in default-design.data.ts (generated
 * from a design authored in the Customize editor — see the export script);
 * this module owns the machinery around it.
 *
 * The design is static: per-user values flow in through `{{agency.*}}` /
 * `{{recruiter.*}}` tokens resolved at render time, and the brand color a
 * user picks (trial form / onboarding) is applied by substituting every
 * occurrence of DEFAULT_DESIGN_BRAND_COLOR via applyDesignBrandColor.
 *
 * This checked-in design can be overridden at runtime by an admin-uploaded
 * default (admin console → Default design;
 * server/src/services/default-design-service.ts), which then drives both
 * new-agency seeding and the public trial page
 * (GET /resumes/trial/default-design); the checked-in design remains the
 * fallback everywhere.
 */
export const DEFAULT_DESIGN: SavedDesign = DEFAULT_DESIGN_DATA

/**
 * The sentinel color inside DEFAULT_DESIGN that a user's brand color
 * replaces. When authoring a new default in the Customize editor, use this
 * color everywhere the brand color should apply — component color fields
 * (borders, backgrounds, text color) and inline rich-text colors alike.
 * Substitution is channel-aware: rgba() occurrences of the sentinel keep
 * their alpha (a 15%-opacity sentinel band becomes a 15%-opacity brand
 * band).
 */
export const DEFAULT_DESIGN_BRAND_COLOR = '#52547a'

interface ColorChannels {
  r: number
  g: number
  b: number
  /** 0–1; undefined for hex/rgb() forms (fully opaque). */
  a?: number
}

function parseColorChannels(color: string): ColorChannels | null {
  const hex = color.match(/^#([0-9a-fA-F]{3,8})$/)?.[1]
  if (hex) {
    const full =
      hex.length <= 4
        ? hex
            .split('')
            .map(c => c + c)
            .join('')
        : hex
    const channel = (i: number) => parseInt(full.slice(i * 2, i * 2 + 2), 16)
    return {
      r: channel(0),
      g: channel(1),
      b: channel(2),
      a: full.length === 8 ? channel(3) / 255 : undefined,
    }
  }
  const rgb = color.match(
    /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*(?:,\s*(0|1|0?\.\d+)\s*)?\)$/
  )
  if (rgb) {
    return {
      r: +rgb[1],
      g: +rgb[2],
      b: +rgb[3],
      a: rgb[4] !== undefined ? parseFloat(rgb[4]) : undefined,
    }
  }
  return null
}

function channelsToHex(c: ColorChannels): string {
  const hex = (n: number) => n.toString(16).padStart(2, '0')
  return `#${hex(c.r)}${hex(c.g)}${hex(c.b)}`
}

function colorAt(to: ColorChannels, alpha: number | undefined): string {
  if (alpha === undefined || alpha >= 1) {
    return channelsToHex(to)
  }
  return `rgba(${to.r},${to.g},${to.b},${alpha})`
}

/** Swaps a component color field when its RGB channels match `from`. */
function swapColorValue(
  value: string,
  from: ColorChannels,
  to: ColorChannels
): string {
  const parsed = parseColorChannels(value)
  if (
    !parsed ||
    parsed.r !== from.r ||
    parsed.g !== from.g ||
    parsed.b !== from.b
  ) {
    return value
  }
  return colorAt(to, parsed.a)
}

/**
 * Swaps `from` occurrences inside rich-text HTML content — the editor
 * writes inline colors as `color="#rrggbb"` attributes and
 * `rgba(r, g, b, x)` style values, which component-field swapping never
 * sees.
 */
function swapContentColors(
  content: string,
  from: ColorChannels,
  to: ColorChannels
): string {
  const hexRe = new RegExp(channelsToHex(from), 'gi')
  const rgbaRe = new RegExp(
    `rgba?\\(\\s*${from.r}\\s*,\\s*${from.g}\\s*,\\s*${from.b}\\s*(?:,\\s*(0|1|0?\\.\\d+)\\s*)?\\)`,
    'gi'
  )
  return content
    .replace(hexRe, colorAt(to, undefined))
    .replace(rgbaRe, (_m, alpha?: string) =>
      colorAt(to, alpha !== undefined ? parseFloat(alpha) : undefined)
    )
}

function swapComponentColors(
  component: HeaderComponent,
  from: ColorChannels,
  to: ColorChannels
): HeaderComponent {
  switch (component.type) {
    case 'text':
    case 'watermark':
      return {
        ...component,
        color: swapColorValue(component.color, from, to),
        content: swapContentColors(component.content, from, to),
      }
    case 'container':
      return {
        ...component,
        backgroundColor: swapColorValue(component.backgroundColor, from, to),
        borderTop: {
          ...component.borderTop,
          color: swapColorValue(component.borderTop.color, from, to),
        },
        borderRight: {
          ...component.borderRight,
          color: swapColorValue(component.borderRight.color, from, to),
        },
        borderBottom: {
          ...component.borderBottom,
          color: swapColorValue(component.borderBottom.color, from, to),
        },
        borderLeft: {
          ...component.borderLeft,
          color: swapColorValue(component.borderLeft.color, from, to),
        },
      }
    case 'image':
    case 'resume':
      return component
  }
}

/**
 * Returns `design` with every occurrence of `fromColor` — in component
 * color fields and inside rich-text content, hex or rgba, alpha
 * preserved (matching is on RGB channels; a 15%-opacity tint of the from
 * color becomes a 15%-opacity tint of the to color) — replaced by
 * `toColor`. Unparseable colors and a same-RGB pair are a no-op that
 * returns the design unchanged.
 */
export function swapDesignColor(
  design: SavedDesign,
  fromColor: string,
  toColor: string
): SavedDesign {
  const from = parseColorChannels(fromColor.trim())
  const to = parseColorChannels(toColor.trim())
  if (!from || !to) return design
  if (from.r === to.r && from.g === to.g && from.b === to.b) return design
  const swapPage = (page: SavedDesign['firstPage']) => ({
    components: page.components.map(c => swapComponentColors(c, from, to)),
  })
  return {
    ...design,
    firstPage: swapPage(design.firstPage),
    subsequentPages: swapPage(design.subsequentPages),
  }
}

/**
 * Returns `design` with every occurrence of DEFAULT_DESIGN_BRAND_COLOR
 * replaced by `brandColor` (see swapDesignColor for the matching rules).
 * Returns the design unchanged when no (or an invalid) brand color is
 * given — colors feed inline styles, so anything that doesn't pass the
 * design color schema is ignored rather than propagated.
 */
export function applyDesignBrandColor(
  design: SavedDesign,
  brandColor?: string | null
): SavedDesign {
  const color = brandColor?.trim()
  if (
    !color ||
    color.toLowerCase() === DEFAULT_DESIGN_BRAND_COLOR ||
    !designColorSchema.safeParse(color).success
  ) {
    return design
  }
  return swapDesignColor(design, DEFAULT_DESIGN_BRAND_COLOR, color)
}
